import React, { useCallback, useEffect, useState } from 'react';
import { Layers, ListChecks, PieChart as PieIcon, Receipt } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../../utils/api';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, money, num, fmtDate, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, TotalsCheck,
  DateRange, RefreshButton, codeLabel,
} from '../financeKit';

// Categories are categories, not states, so they need telling apart — muted tints of one family.
const COLOURS = ['#4f46e5', '#64748b', '#818cf8', '#94a3b8', '#3730a3', '#cbd5e1', '#a5b4fc', '#475569'];
const EMPTY = { by_category: [], expenses: [], total: 0, count: 0 };

const day = (v) => fmtDate(v ? String(v).slice(0, 10) : '');

// Chart tooltip: category, how many expenses and the total to the cent.
function CategoryTooltip({ active, payload }) {
  if (!active || !payload || !payload[0]) return null;
  const d = payload[0].payload || {};
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-card-deep">
      <p className="font-semibold text-slate-700">{d.category}</p>
      <p className="mt-0.5 text-slate-600">{plural(d.count, 'expense')}</p>
      <p className="mt-0.5 font-semibold tabular-nums text-slate-900">{money(d.total)}</p>
    </div>
  );
}

export default function ExpensesReport() {
  const [data, setData]   = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/expenses?from=${from}&to=${to}`);
      setData(r.data || EMPTY);
    } catch (e) {
      setData(EMPTY);
      setError(e.response?.data?.message || "We couldn't load the expenses report. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const pieData = (data.by_category || []).map(c => ({
    category: codeLabel(c.category) || 'Uncategorised',
    total: parseFloat(c.total || 0),
    count: parseInt(c.count || 0),
  }));
  const expenses = data.expenses || [];

  // Both tables' totals rows are the plain sum of their rows; the tiles use the report's own total.
  const categoryCount = pieData.reduce((s, c) => s + (c.count || 0), 0);
  const categoryTotal = pieData.reduce((s, c) => s + c.total, 0);
  const logTotal      = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const checks = [
    { label: 'Category summary', computed: categoryTotal, expected: data.total },
    { label: 'Expense log', computed: logTotal, expected: data.total },
  ];

  const avg = (data.count || 0) > 0 ? (parseFloat(data.total || 0) / data.count) : 0;
  const noData = !loading && pieData.length === 0;

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Expenses by category"
        description="Business expenses logged in the range, grouped by category."
        actions={(
          <>
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="expenses-report" />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Total expenses" value={loading ? '…' : money(data.total)} accent="primary"
              sub={loading ? null : 'For the range'} />
            <KpiTile label="Expenses logged" value={loading ? '…' : num(data.count || 0)} accent="slate" />
            <KpiTile label="Categories" value={loading ? '…' : num(pieData.length)} accent="slate"
              sub={loading ? null : 'With spend'} />
            <KpiTile label="Average per expense" value={loading ? '…' : money(avg)} accent="slate" />
          </div>

          {/* Category breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="By category" icon={PieIcon} bodyClass="p-4">
              {loading ? (
                <TableSkeleton rows={5} cols={3} />
              ) : noData ? (
                <EmptyState icon={PieIcon} title="No expenses in this range"
                  hint="Log business costs on the Expenses page so profit reports take them off revenue." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                      dataKey="total" nameKey="category" paddingAngle={2} isAnimationActive={false}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLOURS[i % COLOURS.length]} />)}
                    </Pie>
                    <Tooltip content={<CategoryTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </SectionCard>

            <SectionCard title={`Category summary${loading ? '' : ` · ${pieData.length}`}`} icon={Layers}>
              {loading ? (
                <TableSkeleton rows={5} cols={3} />
              ) : noData ? (
                <EmptyState icon={Layers} title="Nothing to summarise"
                  hint="Each category with spend in the range gets a row here." />
              ) : (
                <div className={TABLE_WRAP}>
                  <table className={TABLE}>
                    <thead>
                      <tr className={THEAD_ROW}>
                        <th scope="col" className={TH}>Category</th>
                        <th scope="col" className={TH_NUM}>Expenses</th>
                        <th scope="col" className={TH_NUM}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pieData.map((row, i) => (
                        <tr key={`${row.category}-${i}`} className={TR}>
                          <td className={TD}>
                            <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: COLOURS[i % COLOURS.length] }} aria-hidden="true" />
                            <span className="font-medium text-slate-800">{row.category}</span>
                          </td>
                          <td className={TD_NUM}>{num(row.count)}</td>
                          <td className={TD_NUM}><MoneyCell value={row.total} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className={TFOOT_ROW}>
                        <td className={TD}>Total</td>
                        <td className={TD_NUM}>{num(categoryCount)}</td>
                        <td className={TD_NUM}><MoneyCell value={categoryTotal} strong /></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>

          {/* Individual expense log */}
          <SectionCard title={`Expense log${loading ? '' : ` · ${expenses.length}`}`} icon={ListChecks}>
            {loading ? (
              <TableSkeleton rows={6} cols={4} />
            ) : expenses.length === 0 ? (
              <EmptyState icon={Receipt} title="No expenses logged for this range"
                hint="Add rent, insurance, software and other running costs on the Expenses page, then come back to see them by category." />
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Date</th>
                      <th scope="col" className={TH}>Category</th>
                      <th scope="col" className={TH}>Description</th>
                      <th scope="col" className={TH_NUM}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((row, i) => (
                      <tr key={row.id || i} className={TR}>
                        <td className={`${TD} whitespace-nowrap`}>{day(row.expense_date)}</td>
                        <td className={TD}>
                          <span className="badge-gray">{codeLabel(row.category_name) || 'Uncategorised'}</span>
                        </td>
                        <td className={`${TD} text-slate-800`}>{row.name || '—'}</td>
                        <td className={TD_NUM}><MoneyCell value={row.amount} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD} colSpan={3}>Total</td>
                      <td className={TD_NUM}><MoneyCell value={logTotal} strong /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>

          {!loading && <TotalsCheck checks={checks} />}
        </>
      )}
    </div>
  );
}
